<template>
  <div :class="[
    'form-field', 
    { 
      'primary-field': isPrimary, 
      'secondary-field': !isPrimary,
      'has-error': error && !initializing,
      'mb-4 md:mb-6': !noMargin
    }
  ]">
    <!-- Label and optional help tooltip -->
    <div class="flex items-start justify-between mb-2">
      <label 
        :for="fieldId" 
        class="block text-responsive-sm font-semibold mb-1 flex items-center"
        :class="isPrimary ? 'text-gray-800' : 'text-gray-600'"
      >
        {{ label }}
        
        <!-- Required marker -->
        <span v-if="required && showRequiredLabel" class="text-red-500 ml-1">*</span>
        
        <!-- Optional marker -->
        <span v-if="!required && showOptionalLabel" class="text-gray-400 ml-1 text-xs">(optional)</span>
        
        <!-- Rich Help Slot OR Default Help Tooltip -->
        <slot name="rich-help-trigger">
          <HelpTooltip
            v-if="helpText" 
            :content="helpText"
            :is-html="helpTextIsHtml"
            :image="helpImage"
            :image-alt="helpImageAlt"
            :aria-label="helpAriaLabel || label + ' help'"
            :position="helpPosition"
          />
        </slot>
      </label>
      
      <!-- Right-aligned slot for additional options -->
      <slot name="label-right"></slot>
    </div>
    
    <!-- Field Content -->
    <div :class="{ 'highlight-new': highlightNew }">
      <slot></slot>
    </div>
    
    <!-- Field Example -->
    <div v-if="example" class="mt-1 text-xs text-gray-500">
      Example: {{ example }}
    </div>
    
    <!-- Field Description -->
    <div v-if="description" class="mt-1 text-xs text-gray-500">
      {{ description }}
    </div>
    
    <!-- Error Message -->
    <p v-if="error" class="mt-1 text-xs text-red-500">
      {{ error }}
    </p>
    
    <!-- Additional Helper Slot -->
    <slot name="helper"></slot>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import HelpTooltip from '@/components/common/HelpTooltip.vue';

const props = defineProps({
  // Field identity
  fieldId: {
    type: String,
    default: () => `field-${Math.random().toString(36).substr(2, 9)}`
  },
  label: {
    type: String,
    required: true
  },
  
  // Field importance
  isPrimary: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  showOptionalLabel: {
    type: Boolean, 
    default: true
  },
  showRequiredLabel: {
    type: Boolean,
    default: true
  },
  
  // Help text
  helpText: {
    type: String,
    default: ''
  },
  helpTextIsHtml: {
    type: Boolean,
    default: false
  },
  helpImage: {
    type: String,
    default: ''
  },
  helpImageAlt: {
    type: String,
    default: ''
  },
  helpAriaLabel: {
    type: String,
    default: ''
  },
  helpPosition: {
    type: String,
    default: 'top'
  },
  
  // Additional context
  example: {
    type: String,
    default: ''
  },
  description: {
    type: String,
    default: ''
  },
  
  // Validation state
  error: {
    type: String,
    default: ''
  },
  
  // New prop to indicate if the form is initializing
  initializing: {
    type: Boolean,
    default: false
  },
  
  // Visual options
  noMargin: {
    type: Boolean,
    default: false
  },
  highlightNew: {
    type: Boolean,
    default: false
  },
  
  // User role for theming
  userRole: {
    type: String,
    default: 'lender', // 'lender' uses primary-xxx classes, 'customer' uses green-xxx
    validator: (value) => ['customer', 'lender'].includes(value)
  }
});

// Highlight animation when a new field appears
const highlightRef = ref(null);
onMounted(() => {
  if (props.highlightNew) {
    highlightRef.value = setTimeout(() => {
      // Animation ends after the CSS animation duration (defined in global styles)
    }, 2000); // Match this to the CSS animation duration
  }
});

// Clean up any references on unmount
import { onBeforeUnmount } from 'vue';
onBeforeUnmount(() => {
  if (highlightRef.value) {
    clearTimeout(highlightRef.value);
  }
});
</script>

<style scoped>
/* Component-specific styles */
.primary-field label {
  @apply font-bold text-gray-800;
}

/* Basic neutral styling only - focus and error styles are set on inputs directly */
.primary-field :deep(input), 
.primary-field :deep(select),
.primary-field :deep(textarea) {
  /* Apply standard neutral border and background by default */
  @apply border border-gray-300 bg-white;
}

.secondary-field label {
  @apply font-medium text-gray-600;
}

/* Error state styling - keep minimal to ensure we don't conflict with direct input styling */
.has-error :deep(input:not(.custom-styled)),
.has-error :deep(select:not(.custom-styled)),
.has-error :deep(textarea:not(.custom-styled)) {
  @apply border-red-300 bg-red-50;
}

/* Note: The highlight-new animation is defined in global styles */
</style> 